import type * as Leaflet from 'leaflet';

import { GeoPosition } from '../../common/Geo';
import { MapAdapter, MapHandle, MapOptions, MapPin, MapViewport } from './map-adapter';

type LeafletModule = typeof Leaflet;

/** Where angular.json copies Leaflet's stylesheet to — relative, so it follows `<base href>` per locale. */
export const LEAFLET_STYLESHEET = 'assets/leaflet/leaflet.css';

/**
 * How long after a programmatic move its `moveend` may still arrive. Leaflet animates a pan or
 * zoom for up to 250 ms; events inside this window are the handle's own and not the user's.
 */
const PROGRAMMATIC_MOVE_MS = 800;

/** A private seller's coordinate is a postcode centroid about a kilometre wide (backend decision 5). */
const POSTAL_CODE_AREA_METRES = 1000;

/**
 * #172: Leaflet 1.9 behind the {@link MapAdapter} seam — the fallback provider since the
 * decision of 2026-10-21 (Google Maps is the default; `environment.map.provider` switches).
 *
 * The library and its stylesheet are fetched the first time a map is created, never in the
 * initial bundle — a results page that stays collapsed on a phone never pays for them. Leaflet
 * is a UMD module (no ESM entry), hence the `default ?? module` dance and the
 * `allowedCommonJsDependencies` entry in angular.json.
 */
export class LeafletMapAdapter implements MapAdapter {
  private library: Promise<LeafletModule> | null = null;

  async create(container: HTMLElement, options: MapOptions): Promise<MapHandle> {
    const L = await this.load();
    return new LeafletMapHandle(L, container, options);
  }

  private load(): Promise<LeafletModule> {
    this.library ??= Promise.all([import('leaflet'), ensureStylesheet(LEAFLET_STYLESHEET)]).then(
      ([module]) => ((module as { default?: LeafletModule }).default ?? module) as LeafletModule,
    );
    return this.library;
  }
}

/** Adds the stylesheet once; a failed load degrades the look, not the map, so it resolves either way. */
function ensureStylesheet(href: string): Promise<void> {
  return new Promise((resolve) => {
    if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) {
      resolve();
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => resolve();
    document.head.appendChild(link);
  });
}

class LeafletMapHandle implements MapHandle {
  private readonly map: Leaflet.Map;
  private readonly pinLayer: Leaflet.LayerGroup;
  private originLayer: Leaflet.CircleMarker | null = null;
  private pinPositions: Leaflet.LatLng[] = [];
  private suppressUntil = 0;

  constructor(
    private readonly L: LeafletModule,
    container: HTMLElement,
    private readonly options: MapOptions,
  ) {
    this.map = L.map(container, {
      center: this.latLng(options.center),
      zoom: options.zoom,
      // Wheel zoom only once the user is "in" the map: an embedded map that scrolls with the
      // page must not swallow the page's own scrolling on the way past.
      scrollWheelZoom: false,
    });
    const tiles = options.config.tiles;
    L.tileLayer(tiles.urlTemplate, { attribution: tiles.attribution, maxZoom: tiles.maxZoom }).addTo(this.map);
    this.pinLayer = L.layerGroup().addTo(this.map);

    this.map.on('focus', () => this.map.scrollWheelZoom.enable());
    this.map.on('blur', () => this.map.scrollWheelZoom.disable());
    this.map.on('mouseout', () => this.map.scrollWheelZoom.disable());
    this.map.on('moveend', () => {
      if (Date.now() < this.suppressUntil) {
        return;
      }
      options.onViewportChange(this.viewport());
    });
  }

  setPins(pins: readonly MapPin[]): void {
    this.pinLayer.clearLayers();
    this.pinPositions = [];
    for (const pin of pins) {
      const latLng = this.latLng(pin.position);
      this.pinPositions.push(latLng);
      const isArea = pin.precision === 'postalCode';
      if (isArea) {
        this.L.circle(latLng, {
          radius: POSTAL_CODE_AREA_METRES,
          className: 'catalog-map-area',
          weight: 1,
          interactive: false,
        }).addTo(this.pinLayer);
      }
      const marker = this.L.marker(latLng, {
        icon: this.L.divIcon({
          className: isArea ? 'catalog-map-pin catalog-map-pin--area' : 'catalog-map-pin',
          // `count` is a number: nothing of the seller's own text reaches innerHTML here.
          html: `<span>${Math.max(0, Math.trunc(pin.count))}</span>`,
          iconSize: [30, 30],
          iconAnchor: [15, 15],
          popupAnchor: [0, -14],
        }),
        title: pin.title,
        alt: pin.title,
        keyboard: true,
      });
      marker.bindPopup(() => this.options.popupContent(pin), { maxWidth: 280 });
      marker.addTo(this.pinLayer);
    }
  }

  setOrigin(position: GeoPosition | null, label: string): void {
    this.originLayer?.remove();
    this.originLayer = null;
    if (!position) {
      return;
    }
    // A text node, not a string: Leaflet renders string tooltip content as HTML.
    const tooltip = document.createElement('span');
    tooltip.textContent = label;
    this.originLayer = this.L.circleMarker(this.latLng(position), {
      radius: 8,
      className: 'catalog-map-origin',
      weight: 3,
    })
      .bindTooltip(tooltip)
      .addTo(this.map);
  }

  fitRadius(center: GeoPosition, radiusKm: number): void {
    this.suppress();
    // toBounds takes the side length of the box: each boundary is half of it from the centre.
    this.map.fitBounds(this.latLng(center).toBounds(radiusKm * 2000), { padding: [12, 12] });
  }

  fitPins(): void {
    if (!this.pinPositions.length) {
      return;
    }
    this.suppress();
    if (this.pinPositions.length === 1) {
      this.map.setView(this.pinPositions[0], 13);
      return;
    }
    this.map.fitBounds(this.L.latLngBounds(this.pinPositions).pad(0.15));
  }

  setView(center: GeoPosition, zoom: number): void {
    this.suppress();
    this.map.setView(this.latLng(center), zoom);
  }

  viewport(): MapViewport {
    const bounds = this.map.getBounds();
    // Zoomed far out, Leaflet reports a box wider than the world; the backend refuses a
    // longitude beyond ±180 (`geoLongitudeOutOfRange`), so the box is clamped to it.
    return {
      north: clamp(bounds.getNorth(), -90, 90),
      south: clamp(bounds.getSouth(), -90, 90),
      east: clamp(bounds.getEast(), -180, 180),
      west: clamp(bounds.getWest(), -180, 180),
      zoom: this.map.getZoom(),
    };
  }

  invalidateSize(): void {
    this.suppress();
    this.map.invalidateSize();
  }

  destroy(): void {
    this.map.remove();
  }

  private suppress(): void {
    this.suppressUntil = Date.now() + PROGRAMMATIC_MOVE_MS;
  }

  private latLng(position: GeoPosition): Leaflet.LatLng {
    return this.L.latLng(position.latitude, position.longitude);
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
